import { ArrowLeft } from 'lucide-react';
import { useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCreateCoverageMutation } from '@/services/api/coverageService';
import {
  CoverageCategory,
  CreateCoverageSchema,
} from '@/services/types/coverage-types';

import { BasicInfoTab } from './forms/BasicInfoTab';
import { BenefitsTab } from './forms/BenefitsTab';
import { ClaimsInfoTab } from './forms/ClaimsInfoTab';
import { CoverageFormTabs } from './forms/CoverageFormTabs';
import { PolicyDetailsTab } from './forms/PolicyDetailsTab';
import { CoverageFormData, createCoverageFormDefaults } from './forms/types';

export const CreateCoveragePage = () => {
  const { matter_id } = useParams<{ matter_id: string }>();
  const navigate = useNavigate();
  const [createCoverage, { isLoading }] = useCreateCoverageMutation();
  // Tab type that matches CoverageFormTabs - CreateCoveragePage doesn't show contacts tab
  const [activeTab, setActiveTab] = useState<
    'basic' | 'contacts' | 'policy' | 'benefits' | 'claims'
  >('basic');

  const { register, handleSubmit, setValue, watch, control } =
    useForm<CoverageFormData>({
      defaultValues: createCoverageFormDefaults(),
    });
  const benefitsFlushRef = useRef<(() => void) | null>(null);

  const {
    fields: insuredFields,
    append: appendInsured,
    remove: removeInsured,
  } = useFieldArray({
    control,
    name: 'named_insured',
  });

  const {
    fields: policyIdFields,
    append: appendPolicyId,
    remove: removePolicyId,
  } = useFieldArray({
    control,
    name: 'policy_identifiers',
  });

  const {
    fields: policyDetailFields,
    append: appendPolicyDetail,
    remove: removePolicyDetail,
  } = useFieldArray({
    control,
    name: 'policy_details',
  });

  const {
    fields: benefitFields,
    append: appendBenefit,
    remove: removeBenefit,
    update: updateBenefit,
  } = useFieldArray({
    control,
    name: 'benefits',
  });

  const {
    fields: vehicleFields,
    append: appendVehicle,
    remove: removeVehicle,
  } = useFieldArray({
    control,
    name: 'vehicles',
  });

  const {
    fields: claimFields,
    append: appendClaim,
    remove: removeClaim,
  } = useFieldArray({
    control,
    name: 'claims',
  });

  const {
    fields: adjusterFields,
    append: appendAdjuster,
    remove: removeAdjuster,
  } = useFieldArray({
    control,
    name: 'adjusters',
  });

  const onSubmit = async (data: CoverageFormData) => {
    if (!matter_id) return;

    try {
      const createData: CreateCoverageSchema = {
        matter_id,
        category: data.category,
        policy_type: data.policy_type,
        carrier_v2_id: data.carrier_v2_id || undefined,
        carrier_division_id: data.carrier_division_id || undefined,
        carrier: data.carrier.name
          ? {
              name: data.carrier.name,
              handles:
                data.carrier.handles && data.carrier.handles.length > 0
                  ? data.carrier.handles
                  : null,
              address: data.carrier.address || null,
            }
          : null,
        policy_identifiers:
          data.policy_identifiers.length > 0 ? data.policy_identifiers : null,
        named_insured:
          data.named_insured
            .map((item) => item.name)
            .filter((name) => name.trim()) || null,
        benefits: data.benefits.length > 0 ? data.benefits : null,
        adjusters: data.adjusters.length > 0 ? data.adjusters : null,
        claims: data.claims.length > 0 ? data.claims : null,
        vehicles: data.vehicles.length > 0 ? data.vehicles : null,
        policy_details:
          data.policy_details.length > 0 ? data.policy_details : null,
        subrogator: data.subrogator.name
          ? {
              name: data.subrogator.name,
              handles:
                data.subrogator.handles && data.subrogator.handles.length > 0
                  ? data.subrogator.handles
                  : null,
              sources:
                data.subrogator.sources && data.subrogator.sources.length > 0
                  ? data.subrogator.sources
                  : null,
            }
          : null,
      };

      await createCoverage({
        matterId: matter_id,
        coverage: createData,
      }).unwrap();

      toast.success('Coverage created successfully');
      navigate(`/matters/${matter_id}/coverage`);
    } catch (error) {
      toast.error('Failed to create coverage');
    }
  };

  // Check if required fields are filled for basic tab
  const isBasicInfoComplete = () => {
    const formData = watch();
    const isPremisesCategory = formData.category === CoverageCategory.PROPERTY;

    // For Property (Premises) coverages, carrier is optional since we may not know it yet
    const hasRequiredCarrier = isPremisesCategory || !!formData.carrier?.name;

    return !!(
      formData.category &&
      formData.policy_type &&
      hasRequiredCarrier &&
      formData.named_insured?.some((item) => item.name?.trim())
    );
  };

  const canSubmit = isBasicInfoComplete();

  const handleCancel = () => {
    navigate(`/matters/${matter_id}/coverage`);
  };

  // Smart tab navigation - CreateCoveragePage only uses basic tabs, not contacts
  const handleNextTab = () => {
    const tabOrder: readonly string[] = [
      'basic',
      'policy',
      'benefits',
      'claims',
    ];
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex >= 0 && currentIndex < tabOrder.length - 1) {
      setActiveTab(tabOrder[currentIndex + 1] as typeof activeTab);
    }
  };

  const handlePreviousTab = () => {
    const tabOrder: readonly string[] = [
      'basic',
      'policy',
      'benefits',
      'claims',
    ];
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabOrder[currentIndex - 1] as typeof activeTab);
    }
  };

  if (!matter_id) {
    return <div>Matter ID not found</div>;
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Coverage
            </Button>
            <div className="h-6 w-px bg-gray-300" />
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-gray-900">
                  Add New Coverage
                </h1>
                {!canSubmit && (
                  <Badge
                    variant="outline"
                    className="text-amber-600 border-amber-300"
                  >
                    Incomplete
                  </Badge>
                )}
                {canSubmit && (
                  <Badge
                    variant="outline"
                    className="text-green-600 border-green-300"
                  >
                    Ready to Save
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Enter the insurance coverage information for this matter
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>Matter ID:</span>
            <code className="bg-gray-100 px-2 py-1 rounded text-xs">
              {matter_id}
            </code>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6">
              <CoverageFormTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                watch={watch}
              />

              <form
                onSubmit={(e) => {
                  benefitsFlushRef.current?.();
                  handleSubmit(onSubmit)(e);
                }}
                className="mt-6"
              >
                <div className="min-h-[500px]">
                  {activeTab === 'basic' && (
                    <BasicInfoTab
                      register={register}
                      setValue={setValue}
                      watch={watch}
                      insuredFields={insuredFields}
                      appendInsured={appendInsured}
                      removeInsured={removeInsured}
                      policyIdFields={policyIdFields}
                      appendPolicyId={appendPolicyId}
                      removePolicyId={removePolicyId}
                    />
                  )}

                  {activeTab === 'policy' && (
                    <PolicyDetailsTab
                      register={register}
                      policyDetailFields={policyDetailFields}
                      appendPolicyDetail={appendPolicyDetail}
                      removePolicyDetail={removePolicyDetail}
                      vehicleFields={vehicleFields}
                      appendVehicle={appendVehicle}
                      removeVehicle={removeVehicle}
                    />
                  )}

                  {activeTab === 'benefits' && (
                    <BenefitsTab
                      setValue={setValue}
                      watch={watch}
                      updateBenefit={updateBenefit}
                      benefitFields={benefitFields}
                      appendBenefit={appendBenefit}
                      removeBenefit={removeBenefit}
                      flushRef={benefitsFlushRef}
                    />
                  )}

                  {activeTab === 'claims' && (
                    <ClaimsInfoTab
                      register={register}
                      setValue={setValue}
                      watch={watch}
                      claimFields={claimFields}
                      appendClaim={appendClaim}
                      removeClaim={removeClaim}
                      adjusterFields={adjusterFields}
                      appendAdjuster={appendAdjuster}
                      removeAdjuster={removeAdjuster}
                    />
                  )}
                </div>

                {/* Enhanced Actions */}
                <div className="flex items-center justify-between pt-6 mt-6 border-t bg-gray-50 -m-6 p-6 rounded-b-lg">
                  {/* Tab Navigation */}
                  <div className="flex gap-2">
                    {activeTab !== 'basic' && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handlePreviousTab}
                        disabled={isLoading}
                      >
                        Previous
                      </Button>
                    )}
                    {activeTab !== 'claims' && isBasicInfoComplete() && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleNextTab}
                        disabled={isLoading}
                      >
                        Next
                      </Button>
                    )}
                  </div>

                  {/* Primary Actions */}
                  <div className="flex items-center gap-3">
                    {!canSubmit && (
                      <div className="text-sm text-amber-600 bg-amber-50 px-3 py-2 rounded border border-amber-200">
                        Complete the Basic Info tab to save this coverage
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading || !canSubmit}
                      className="min-w-32"
                    >
                      {isLoading ? 'Creating...' : 'Create Coverage'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
